import React from 'react';
import { useI18n } from '../../i18n/I18n';

interface ConfirmModalProps {
    isOpen: boolean;
    /** Translation keys — every user-facing string goes through the resolver. */
    titleKey: string;
    messageKey: string;
    confirmKey: string;
    cancelKey: string;
    /** Untranslated data (an agent name, a webhook URL) shown under the message. */
    detail?: string;
    /** Renders the confirm action in red, for irreversible operations. */
    destructive?: boolean;
    onConfirm: () => void;
    onClose: () => void;
}

/**
 * Reusable confirmation dialog, styled after AddEndpointModal (backdrop click to
 * dismiss, no portal). Copy is passed as translation keys so callers cannot leak
 * hardcoded English into the modal.
 */
export default function ConfirmModal({
    isOpen,
    titleKey,
    messageKey,
    confirmKey,
    cancelKey,
    detail,
    destructive = false,
    onConfirm,
    onClose,
}: ConfirmModalProps) {
    const { t } = useI18n();

    if (!isOpen) return null;

    const handleContainerClick = (e: React.MouseEvent) => {
        // Prevent closing when clicking inside the modal
        e.stopPropagation();
    };

    const confirmClassName = destructive
        ? 'flex-1 py-3 rounded-xl text-[15px] font-bold text-white bg-red-500 hover:bg-red-500/90 border border-transparent shadow-[0_0_15px_rgba(239,68,68,0.3)] transition-all cursor-pointer'
        : 'flex-1 py-3 rounded-xl text-[15px] font-bold text-white bg-electric-blue hover:bg-electric-blue/90 border border-transparent shadow-[0_0_15px_rgba(19,127,236,0.3)] transition-all cursor-pointer';

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={onClose}
        >
            <div
                className="w-full max-w-sm rounded-3xl p-6 shadow-2xl flex flex-col gap-6"
                style={{ background: 'var(--modal-bg)', border: '1px solid var(--color-surface-border)' }}
                onClick={handleContainerClick}
            >
                {/* Header */}
                <div className="flex flex-col gap-2">
                    <h2 className="text-xl font-bold text-txt-heading tracking-tight">{t(titleKey)}</h2>
                    <p className="text-[15px] text-txt-secondary leading-snug">{t(messageKey)}</p>
                    {detail && (
                        <p className="text-[15px] font-bold text-txt-heading break-all">{detail}</p>
                    )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 pt-2">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 rounded-xl text-[15px] font-bold text-txt-heading bg-transparent hover:bg-surface-hover transition-colors cursor-pointer"
                        style={{ border: '1px solid var(--color-surface-border)' }}
                    >
                        {t(cancelKey)}
                    </button>
                    <button onClick={onConfirm} className={confirmClassName}>
                        {t(confirmKey)}
                    </button>
                </div>
            </div>
        </div>
    );
}
